import { SelectProps } from '@cloudscape-design/components';
import { Persona } from './Persona';

export const APP_PROGRAM_ID = 'SAT';
export const DEFAULT_LANGUAGE = 'en-US';

/** Polling interval for multi-assignee collaboration features (ms) */
export const POLL_INTERVAL_MS = 15000;

// TODO: Replace with correct URL once product confirms (Q3 in clarification questions)
export const CI_PORTAL_URL =
  'https://ci.corp.amazon.com/help?referrer=gravion_100_audit_button';

export enum LOCAL_STORAGE_KEYS {
  BUSINESS_UNIT = 'BUSINESS_UNIT',
  MIDWAY_AUTH_USERNAME = 'MIDWAY_AUTH_USERNAME',
  USER_INFO = 'USER_INFO',
  TIME_ZONE = 'TIME_ZONE',
}

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const DATE_FORMATS = {
  DEFAULT: 'DD MMM YYYY hh:mm a',
  SHORT_DATE: 'DD MMM YYYY',
  TIME_ONLY: 'hh:mm a',
};

export const DATE_TOKENS = {
  YEAR: 'YYYY',
  MONTH: 'MM',
  MONTH_SHORT: 'MMM',
  DAY: 'DD',
  HOUR_24: 'HH',
  HOUR_12: 'hh',
  MINUTE: 'mm',
  SECOND: 'ss',
  MERIDIEM: 'a',
};

export const ERROR_MESSAGES = {
  DEFAULT:
    'The connection could not be established. Please try again or refresh the application.',
  INVALID_DATE: '-',
  INVALID_INPUT: 'Invalid input provided',
  ASSIGNEE_AT_SYMBOL: 'Please enter valid user alias without @amazon.com',
};

/**
 * Error codes that indicate retryable network issues
 */
export const RETRYABLE_ERROR_CODES = {
  ERR_CANCELED: 'ERR_CANCELED',
  ECONNABORTED: 'ECONNABORTED',
  ERR_NETWORK: 'ERR_NETWORK',
  ETIMEDOUT: 'ETIMEDOUT',
} as const;

/**
 * Error message patterns that indicate retryable network issues
 */
export const RETRYABLE_ERROR_PATTERNS = [
  'aborted',
  'cancelled',
  'canceled',
  'network error',
  'timeout',
  'econnreset',
  'econnrefused',
] as const;

export const ASSIGNEE_LABELS = {
  PLACEHOLDER: 'Enter assignee alias (without @amazon.com)',
  LABEL: 'Assignee',
};

export const VALIDATION_PATTERNS = {
  ASSIGNEE_ALIAS: /^[a-zA-Z0-9]+$/,
};

export enum AuditTypeOptions {
  TEST = 'TEST',
  PROGRESS = 'PROGRESS',
  SITUATION = 'SITUATION',
  CALIBRATION = 'CALIBRATION',
  MPA = 'MPA',
}

export const AUDIT_TYPE_LABELS = {
  [AuditTypeOptions.TEST]: 'Test',
  [AuditTypeOptions.PROGRESS]: 'Progress',
  [AuditTypeOptions.SITUATION]: 'Situation',
  [AuditTypeOptions.CALIBRATION]: 'Calibration',
  [AuditTypeOptions.MPA]: 'MPA',
};

export const MARKDOWN_TEXT_WARNING = '';

export enum EntityType {
  QUESTION = 'QUESTION',
  ANSWER = 'ANSWER',
  TASK = 'TASK',
}

export const AUDIT_SCORES_OPTIONS: SelectProps.Option[] = [
  { value: '1' },
  { value: '2' },
  { value: '3' },
  { value: '4' },
  { value: '5' },
  { value: '6' },
  { value: '7' },
  { value: '8' },
  { value: '9' },
  { value: '10' },
  { value: '11' },
  { value: '12' },
  { value: '13' },
  { value: '14' },
  { value: '15' },
  { value: '16' },
  { value: '17' },
  { value: '18' },
  { value: '19' },
  { value: '20' },
];

export const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];

export const LANGUAGE_LIST: SelectProps.Option[] = [
  {
    value: 'en-US',
    label: 'English',
  },
  {
    value: 'de-DE',
    label: 'German',
  },
  {
    value: 'es-ES',
    label: 'Spanish',
  },
  {
    value: 'fr-FR',
    label: 'French',
  },
  {
    value: 'ja-JP',
    label: 'Japanese',
  },
  {
    value: 'it-IT',
    label: 'Italian',
  },
  {
    value: 'pt-PT',
    label: 'Portuguese',
  },
  {
    value: 'pl-PL',
    label: 'Polish',
  },
];

export enum ScheduleType {
  ONCE = 'ONCE',
  RECURRING = 'RECURRING',
}

export const SCHDULE_TYPE_LABELS = {
  [ScheduleType.RECURRING]: 'Recurring',
  [ScheduleType.ONCE]: 'Once',
};

export const weekDays = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' },
];

export const recurrenceOptions = [
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Yearly', value: 'YEARLY' },
];

export enum AuditStatus {
  NOT_STARTED = 'NOT_STARTED',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

export const AUDIT_STATUS_LABELS = {
  [AuditStatus.NOT_STARTED]: 'Not Started',
  [AuditStatus.SCHEDULED]: 'Scheduled',
  [AuditStatus.IN_PROGRESS]: 'In Progress',
  [AuditStatus.COMPLETED]: 'Completed',
  [AuditStatus.CANCELLED]: 'Cancelled',
  [AuditStatus.EXPIRED]: 'Expired',
};

export enum ScheduleStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  PAUSED = 'PAUSED',
  INACTIVE = 'INACTIVE',
}

export const SCHEDULE_STATUS_LABELS = {
  [ScheduleStatus.SCHEDULED]: 'Scheduled',
  [ScheduleStatus.IN_PROGRESS]: 'In Progress',
  [ScheduleStatus.COMPLETED]: 'Completed',
  [ScheduleStatus.PAUSED]: 'Paused',
  [ScheduleStatus.INACTIVE]: 'Inactive',
};

import { TranslationStatus } from '@amzn/gbsa-helios-typescript-client';

export const TRANSLATION_STATUS_LABELS: Record<string, string> = {
  [TranslationStatus.NOT_STARTED]: 'Not Started',
  [TranslationStatus.IN_PROGRESS]: 'In Progress',
  [TranslationStatus.COMPLETE]: 'Complete',
};

// Re-export from TypeScript client for backward compatibility
// Note: Client uses ResponseResult; aliased as QuestionResultType for existing consumers
export { ResponseResult as QuestionResultType } from '@amzn/gbsa-helios-typescript-client';
import { ResponseResult as QuestionResultType } from '@amzn/gbsa-helios-typescript-client';
export const QUESTION_TYPE_LABELS = {
  [QuestionResultType.PASS]: 'Pass',
  [QuestionResultType.FAIL]: 'Fail',
  [QuestionResultType.NA]: 'N/A',
};
export enum ScoreFilterType {
  PASS = 'pass',
  FAIL = 'fail',
  NA = 'na',
  TOTAL = 'total',
}

export enum TaskPriority {
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low',
}

export { ResponseStatus } from '@amzn/gbsa-helios-typescript-client';

export { TaskType } from '@amzn/gbsa-helios-typescript-client';
import { TaskType } from '@amzn/gbsa-helios-typescript-client';

export const TASK_TYPE_LABELS = {
  [TaskType.OBSERVATION]: 'Observation',
  [TaskType.ADHOC]: 'Adhoc',
  [TaskType.FAILED_QUESTION]: 'Failed Question',
};

export const AUDIT_TYPES = [
  { label: 'All', value: 'all' },
  { label: 'Situational', value: 'situational' },
  { label: 'Progress', value: 'progress' },
  { label: 'Calibration', value: 'calibration' },
  { label: 'Test', value: 'test' },
  { label: 'MPA', value: 'mpa' },
] as const;

export const ROLES_LABELS = {
  [Persona.PLATFORM_ADMIN]: 'Platform Admin',
  [Persona.BUSINESS_UNIT_ADMIN]: 'Business Unit Admin',
  [Persona.CONTENT_ADMIN]: 'Content Admin',
  [Persona.AUDIT_ADMIN]: 'Audit Admin',
  [Persona.SUPER_AUDITOR]: 'Super Auditor',
  [Persona.AUDITOR]: 'Auditor',
  [Persona.TASK_MANAGER]: 'Task Manager',
};

export const COUNTRY_OPTIONS: SelectProps.Option[] = [
  { value: 'Egypt', label: 'Egypt' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'Turkey', label: 'Turkey' },
  { value: 'Austria', label: 'Austria' },
  { value: 'Belgium', label: 'Belgium' },
  { value: 'Bulgaria', label: 'Bulgaria' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Czechia', label: 'Czechia' },
  { value: 'Germany', label: 'Germany' },
  { value: 'Denmark', label: 'Denmark' },
  { value: 'Estonia', label: 'Estonia' },
  { value: 'Spain', label: 'Spain' },
  { value: 'Finland', label: 'Finland' },
  { value: 'France', label: 'France' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'Greece', label: 'Greece' },
  { value: 'Croatia', label: 'Croatia' },
  { value: 'Ireland', label: 'Ireland' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Luxembourg', label: 'Luxembourg' },
  { value: 'Malta', label: 'Malta' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'Poland', label: 'Poland' },
  { value: 'Portugal', label: 'Portugal' },
  { value: 'Romania', label: 'Romania' },
  { value: 'Sweden', label: 'Sweden' },
  { value: 'Slovakia', label: 'Slovakia' },
  { value: 'China', label: 'China' },
  { value: 'Japan', label: 'Japan' },
  { value: 'Malaysia', label: 'Malaysia' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Viet Nam', label: 'Viet Nam' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Hong Kong', label: 'Hong Kong' },
  { value: 'India', label: 'India' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'United States', label: 'United States' },
  { value: 'Costa Rica', label: 'Costa Rica' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Chile', label: 'Chile' },
  { value: 'Colombia', label: 'Colombia' },
];

export const REGION_OPTIONS: SelectProps.Option[] = [
  { value: 'AMET', label: 'AMET' },
  { value: 'Europe', label: 'Europe' },
  { value: 'Far East', label: 'Far East' },
  { value: 'India', label: 'India' },
  { value: 'North America', label: 'North America' },
  { value: 'South America', label: 'South America' },
];

// Region to Country mapping
export const REGION_COUNTRY_MAPPING: Record<string, string[]> = {
  'AMET': ['Egypt', 'South Africa', 'United Arab Emirates', 'Saudi Arabia', 'Turkey'],
  'Europe': [
    'Austria',
    'Belgium',
    'Bulgaria',
    'Switzerland',
    'Czechia',
    'Germany',
    'Denmark',
    'Estonia',
    'Spain',
    'Finland',
    'France',
    'United Kingdom',
    'Greece',
    'Croatia',
    'Ireland',
    'Italy',
    'Luxembourg',
    'Malta',
    'Netherlands',
    'Poland',
    'Portugal',
    'Romania',
    'Sweden',
    'Slovakia',
  ],
  'Far East': [
    'China',
    'Japan',
    'Malaysia',
    'Singapore',
    'Viet Nam',
    'Australia',
    'Hong Kong',
  ],
  'India': ['India'],
  'North America': ['Canada', 'Mexico', 'Puerto Rico', 'United States'],
  'South America': ['Costa Rica', 'Brazil', 'Chile', 'Colombia'],
};

// Function to get countries by region
export const getCountriesByRegion = (region: string): SelectProps.Option[] => {
  const countries = REGION_COUNTRY_MAPPING[region] || COUNTRY_OPTIONS;
  return countries.map((country) => ({ value: country, label: country }));
};

export enum AuditDisableMessages {
  AUDIT_COMPLETED = 'This audit is no longer available for modification as it has been completed.',
  NO_ASSIGNEE = 'No user has been assigned to this audit. Please assign a user to continue',
  WRONG_ASSIGNEE = 'This audit is assigned to {assignee}. Only the assigned user can perform this audit.',
  WRONG_ASSIGNEE_MULTI = 'This audit is assigned to other users. Only assigned users can perform this audit.',
  NO_PERMISSION = 'You do not have permission to perform the audit.',
}

export enum AuditTooltipMessages {
  QUESTION_OVERVIEW = 'Provide a brief overview of what this audit question aims to verify. This should be concise yet descriptive enough for auditors to understand the purpose. 50 Char Limit',

  DIVISION = 'Select the business division this question is built for. Your audit template will pull questions based on division.',

  CATEGORY = 'Choose the relevant categories. This helps in grouping related questions and generating category-specific reports.',

  AUDIT_SCORE = "Audit score determines question's importance and weight in the audit.",

  QUESTION_TEXT = 'Write the actual audit question clearly and concisely. Use simple, unambiguous language that any auditor can understand. Should have pass/fail/n.a. response.',

  AUDIT_INSTRUCTION = 'Provide step-by-step guidance on how to perform this audit check. Include specific details about what to observe, measure, or verify.',

  PASS_CRITERIA = 'Define exactly what conditions must be met to pass this audit question. Be specific and measurable.',

  FAIL_CRITERIA = 'Clearly state what conditions constitute a failure. Include specific thresholds or circumstances that would result in a failed audit question.',

  JUSTIFICATION = 'Explain why this audit question is important and how it relates to business objectives, compliance requirements, or standards.',

  SUB_QUESTION = 'Add related follow-up questions if needed. The Audit score will be equally distributed to each sub question.',

  TAGS_INCLUDED = 'Add relevant tags to help link questions with audits utilizing templates.',

  TAGS_EXCLUDED = 'Add relevant tags to help exclude questions with audits utilizing templates.',

  LOCATIONS_EXCLUDED = 'Specify any locations where this audit question should not apply, such as sites with different processes or requirements.',

  DOCUMENTATION = 'Include links of reference materials, SOPs, or guidelines that support this audit question.',
}

/**
 * Question field names used across the application
 * These field names are used in forms, API requests, and data structures
 */
export const QUESTION_FIELD_NAMES = {
  // Question Details Fields
  TITLE: 'title',
  QUESTION_TEXT: 'questionText',
  AUDIT_INSTRUCTION: 'auditInstruction',
  PASS_CRITERIA: 'passCriteria',
  FAIL_CRITERIA: 'failCriteria',
  JUSTIFICATION: 'justification',

  // Task Details Fields
  TASK_NAME: 'taskName',
  TASK_DESCRIPTION: 'taskDescription',
  TASK_ID: 'taskId',

  // Sub Question Fields
  SUB_QUESTION_ID: 'subQuestionId',
  INSTRUCTION: 'instruction',

  // Nested Paths
  TASK_DETAILS: 'taskDetails',
  QUESTION_DETAILS: 'questionDetails',
  SUB_QUESTIONS: 'subQuestions',
} as const;

/**
 * Display labels for question fields
 * Used in UI components and validation messages
 */
export const QUESTION_FIELD_LABELS = {
  [QUESTION_FIELD_NAMES.TITLE]: 'Title',
  [QUESTION_FIELD_NAMES.QUESTION_TEXT]: 'Question Text',
  [QUESTION_FIELD_NAMES.AUDIT_INSTRUCTION]: 'Audit Instruction',
  [QUESTION_FIELD_NAMES.PASS_CRITERIA]: 'Pass Criteria',
  [QUESTION_FIELD_NAMES.FAIL_CRITERIA]: 'Fail Criteria',
  [QUESTION_FIELD_NAMES.JUSTIFICATION]: 'Justification',
  [QUESTION_FIELD_NAMES.TASK_NAME]: 'Task Name',
  [QUESTION_FIELD_NAMES.TASK_DESCRIPTION]: 'Task Description',
  [QUESTION_FIELD_NAMES.INSTRUCTION]: 'Instruction',
} as const;
